import { TeachingBanner } from "../components/TeachingBanner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Briefcase,
  Calendar,
  Check,
  Clock,
  Flag,
  HelpCircle,
  Layers,
  Inbox,
  MessageSquare,
  PauseCircle,
  Play,
  Sparkles,
  Users,
  X as XIcon,
} from "lucide-react";
import { api, type WeaveInsight } from "../lib/api";
import { TeachingNote } from "../components/TeachingNote";
import { L2RailShell, type L2NavItem } from "../components/L2Nav";
import { Breadcrumb } from "../components/Breadcrumb";

type EngagementKind =
  | "meeting_prep"
  | "deliverable"
  | "commitment"
  | "promise"
  | "request_pending"
  | "milestone"
  | "decision_pending"
  | "follow_up"
  | "project"
  | "conversation"
  | "other";

type EngagementStatus = "active" | "completed" | "abandoned" | "postponed";

const KIND_ICON: Record<EngagementKind, typeof Sparkles> = {
  meeting_prep: Calendar,
  deliverable: Briefcase,
  commitment: Flag,
  promise: Flag,
  request_pending: HelpCircle,
  milestone: Flag,
  decision_pending: Layers,
  follow_up: MessageSquare,
  project: Briefcase,
  conversation: MessageSquare,
  other: Sparkles,
};

const KIND_LABEL: Record<EngagementKind, string> = {
  meeting_prep: "Meeting prep",
  deliverable: "Deliverable",
  commitment: "Commitment",
  promise: "Promise",
  request_pending: "Request pending",
  milestone: "Milestone",
  decision_pending: "Decision pending",
  follow_up: "Follow-up",
  project: "Project",
  conversation: "Conversation",
  other: "Other",
};

interface Engagement {
  id: string;
  kind: string;
  title: string;
  description: string | null;
  status: string;
  scope: string | null;
  due_at: string | null;
  detection_source: string;
  detection_reason: string | null;
  last_activity_at: string | null;
  activity_count: number;
  created_at: string;
}

/** The page is the canonical "all engagements" archive. Each tab is
 *  purely a filter on engagements.kind over the SAME base table the
 *  Engagement Stream reads from (reconciles ambient-assessment §1.10:
 *  the page used to query only meeting_prep + commitment and showed
 *  "No engagements yet" while the Stream showed 20 conversations).
 *  Default tab = All. */
const KIND_TABS: ReadonlyArray<L2NavItem & { kind?: string }> = [
  { id: "all", label: "All", Icon: Inbox, kind: undefined, hint: "Every engagement Memso tracks." },
  { id: "meetings", label: "Meetings", Icon: Calendar, kind: "meeting_prep", hint: "Calendar invites and prep arcs." },
  { id: "commitments", label: "Commitments", Icon: Flag, kind: "commitment", hint: "Promises in flight, yours and theirs." },
  { id: "conversations", label: "Conversations", Icon: MessageSquare, kind: "conversation", hint: "Threaded discussions Memso clustered." },
  { id: "followups", label: "Follow-ups", Icon: HelpCircle, kind: "follow_up", hint: "Threads waiting on someone." },
];

export function Engagements() {
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);
  const navigate = useNavigate();
  const params = useParams<{ handle?: string; tab?: string }>();
  const handlePrefix = params.handle ? `/u/${params.handle}` : "";
  // URL-routed L2 tab: /engagements/:tab. Falls back to "all" if the
  // path segment is missing or doesn't map to a known tab. PUSH on
  // change so back returns to the prior tab.
  const tab = (() => {
    const raw = params.tab;
    if (raw && KIND_TABS.some((t) => t.id === raw)) return raw;
    return "all";
  })();
  const setTab = useCallback(
    (next: string) => {
      const base = `${handlePrefix}/engagements`;
      navigate(next === "all" ? base : `${base}/tab/${next}`);
    },
    [handlePrefix, navigate],
  );

  const load = useCallback(() => {
    const kind = KIND_TABS.find((t) => t.id === tab)?.kind;
    api
      .engagements({ status: "active", kind, limit: 100 })
      .then((d) => {
        setEngagements(d.engagements);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [tab]);

  useEffect(load, [load]);

  // Recurring meetings (Honda VAN VEIP, weekly standups, monthly bills)
  // can land as N separate engagement rows when the detector treats
  // each calendar instance as its own engagement. Fold them by
  // normalized (kind, title) so the user sees one card per series with
  // a "then" tail listing later occurrences. Single engagements pass
  // through unchanged.
  const grouped = useMemo(() => groupRecurringEngagements(engagements), [engagements]);

  const detect = async () => {
    if (detecting) return;
    setDetecting(true);
    try {
      await api.engagementsDetect();
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDetecting(false);
    }
  };

  const activeTabItem = KIND_TABS.find((t) => t.id === tab) ?? KIND_TABS[0];
  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb
        segments={
          tab === "all"
            ? [{ label: "Engagements" }]
            : [
                { label: "Engagements", href: "/engagements" },
                { label: activeTabItem.label },
              ]
        }
      />
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {activeTabItem.id === "all" ? "All engagements" : activeTabItem.label}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Every long-running thread Memso tracks: meetings to prep, commitments to keep, conversations and follow-ups in flight. Same source as your Today stream, filtered by type.
          </p>
        </div>
        <button
          type="button"
          onClick={detect}
          disabled={detecting}
          className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-2 shadow-sm disabled:opacity-50"
          title="Scan recent calendar invites + commitments and surface candidate engagements"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {detecting ? "Detecting..." : "Detect now"}
        </button>
      </header>

      <L2RailShell
        items={KIND_TABS}
        activeId={tab}
        onPick={setTab}
        railAriaLabel="Engagement kind"
        listAriaLabel="Pick an engagement category"
        tint="orange"
        mobileHeader={
          <>
            <TeachingBanner id="engagements_overview" />
            <TeachingNote
              id="engagements.v1"
              tone="emerald"
              title="Engagements connect captures into ongoing threads."
              body="Memso clusters meetings, deliverables, commitments, and decisions so a single project shows you all related captures, deadlines, and people in one frame. Detect now scans recent calendar invites and commitments to surface fresh candidates."
            />
          </>
        }
      >
        <div className="space-y-4">
          <TeachingBanner id="engagements_overview" />
          <TeachingNote
            id="engagements.v1"
            tone="emerald"
            title="Engagements connect captures into ongoing threads."
            body="Memso clusters meetings, deliverables, commitments, and decisions so a single project shows you all related captures, deadlines, and people in one frame. Detect now scans recent calendar invites and commitments to surface fresh candidates."
          />

          {error && (
            <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>
          )}

          {grouped.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-2">
              {grouped.map((g) => (
                <EngagementCard
                  key={g.primary.id}
                  engagement={g.primary}
                  laterOccurrences={g.later}
                  onOpen={() => navigate(`${handlePrefix}/engagements/${g.primary.id}`)}
                />
              ))}
            </ul>
          )}
        </div>
      </L2RailShell>
    </div>
  );
}

function EmptyState() {
  return (
    <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 text-center">
      <Briefcase className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-500" strokeWidth={2.5} />
      <h2 className="mt-3 text-lg font-bold text-slate-900 dark:text-slate-100">
        No engagements yet.
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
        Memso surfaces engagements from your calendar invites + detected commitments. Hit "Detect now" to scan recent activity.
      </p>
    </section>
  );
}

interface EngagementGroup {
  primary: Engagement;
  later: Engagement[];
}

/** Fold same-title same-kind engagements into one card per series so a
 *  weekly Honda VAN VEIP doesn't render as four separate cards
 *  ("in 3d", "in 10d", "in 17d", "in 24d"). The earliest non-overdue
 *  instance becomes the primary; the rest surface as a "then" tail. */
function groupRecurringEngagements(es: Engagement[]): EngagementGroup[] {
  const buckets = new Map<string, Engagement[]>();
  const order: string[] = [];
  for (const e of es) {
    const key = recurrenceKey(e);
    const bucket = buckets.get(key);
    if (!bucket) {
      buckets.set(key, [e]);
      order.push(key);
    } else {
      bucket.push(e);
    }
  }
  const out: EngagementGroup[] = [];
  for (const key of order) {
    const list = buckets.get(key)!;
    if (list.length === 1) {
      out.push({ primary: list[0], later: [] });
      continue;
    }
    const sorted = [...list].sort((a, b) => dueAtMs(a) - dueAtMs(b));
    out.push({ primary: sorted[0], later: sorted.slice(1) });
  }
  return out;
}

function recurrenceKey(e: Engagement): string {
  const t = (e.title || "").trim().toLowerCase().replace(/\s+/g, " ");
  return `${e.kind}::${t}`;
}

function dueAtMs(e: Engagement): number {
  if (!e.due_at) return Number.POSITIVE_INFINITY;
  const ms = new Date(e.due_at).getTime();
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

function EngagementCard({
  engagement,
  laterOccurrences = [],
  onOpen,
}: {
  engagement: Engagement;
  laterOccurrences?: Engagement[];
  onOpen: () => void;
}) {
  const Icon = KIND_ICON[(engagement.kind as EngagementKind) ?? "other"] ?? Sparkles;
  const label = KIND_LABEL[(engagement.kind as EngagementKind) ?? "other"] ?? engagement.kind;
  const dueLabel = formatDue(engagement.due_at);
  const isOverdue =
    !!engagement.due_at && new Date(engagement.due_at).getTime() < Date.now() && engagement.status === "active";
  const laterLabel = formatLaterOccurrences(laterOccurrences);
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-3 sm:p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
      >
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white shadow-sm flex-shrink-0">
            <Icon className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-orange-200 dark:bg-orange-700/40 text-orange-800 dark:text-orange-200">
                {label}
              </span>
              <span className={`text-[10px] uppercase tracking-wider font-bold ${statusTint(engagement.status)}`}>
                {engagement.status}
              </span>
              {dueLabel && (
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${isOverdue ? "text-rose-700 dark:text-rose-300" : "text-slate-600 dark:text-slate-300"}`}>
                  <Clock className="w-2.5 h-2.5" />
                  {dueLabel}
                </span>
              )}
              {engagement.activity_count > 0 && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400">
                  {engagement.activity_count} activity
                </span>
              )}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100 break-words">
              {engagement.title}
            </div>
            {engagement.description && (
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 line-clamp-2 whitespace-pre-wrap">
                {engagement.description}
              </div>
            )}
            {laterLabel && (
              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {laterLabel}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function formatLaterOccurrences(later: Engagement[]): string | null {
  if (later.length === 0) return null;
  const labels = later
    .map((e) => formatDue(e.due_at))
    .filter((s): s is string => !!s)
    .slice(0, 3);
  if (labels.length === 0) return `${later.length} more occurrence${later.length === 1 ? "" : "s"}`;
  const head = labels.length === 1 ? `then ${labels[0]}` : `then ${labels.join(", then ")}`;
  if (later.length > labels.length) return `${head} (+${later.length - labels.length} more)`;
  return head;
}

function statusTint(s: string): string {
  switch (s) {
    case "active": return "text-emerald-700 dark:text-emerald-300";
    case "completed": return "text-sky-700 dark:text-sky-300";
    case "abandoned": return "text-rose-700 dark:text-rose-300";
    case "postponed": return "text-amber-700 dark:text-amber-300";
    default: return "text-slate-500 dark:text-slate-400";
  }
}

function formatDue(due: string | null): string | null {
  if (!due) return null;
  const ms = new Date(due).getTime();
  if (Number.isNaN(ms)) return null;
  const delta = ms - Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (delta < -day) return `Overdue ${Math.round(-delta / day)}d`;
  if (delta < 0) return "Overdue";
  if (delta < 60 * 60 * 1000) return `In ${Math.round(delta / 60000)}m`;
  if (delta < day) return `In ${Math.round(delta / 3600000)}h`;
  return `In ${Math.round(delta / day)}d`;
}

// =====================================================================
// Detail page (lazy import target)
// =====================================================================

interface EngagementDetail {
  id: string;
  kind: string;
  title: string;
  description: string | null;
  status: string;
  scope: string | null;
  due_at: string | null;
  detection_source: string;
  detection_reason: string | null;
  last_activity_at: string | null;
  activity_count: number;
  created_at: string;
}

interface ActivityRow {
  id: string;
  kind: string;
  summary: string;
  occurred_at: string;
  source_ref_json: string | null;
  payload_json: string | null;
}

export function EngagementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const params = useParams<{ handle?: string }>();
  const handlePrefix = params.handle ? `/u/${params.handle}` : "";
  const [engagement, setEngagement] = useState<EngagementDetail | null>(null);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [weaveInsights, setWeaveInsights] = useState<WeaveInsight[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => {
    if (!id) return;
    api
      .engagement(id)
      .then((d) => {
        setEngagement(d.engagement);
        setActivity(d.activity);
        setWeaveInsights(d.weave_insights ?? []);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [id]);

  useEffect(load, [load]);

  const flip = async (status: EngagementStatus) => {
    if (!id || busy) return;
    setBusy(true);
    try {
      await api.engagementStatus(id, status);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!id || !noteText.trim() || busy) return;
    setBusy(true);
    try {
      await api.engagementAddActivity(id, noteText.trim());
      setNoteText("");
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const Icon = useMemo(
    () => KIND_ICON[(engagement?.kind as EngagementKind) ?? "other"] ?? Sparkles,
    [engagement?.kind],
  );
  const label = KIND_LABEL[(engagement?.kind as EngagementKind) ?? "other"] ?? engagement?.kind ?? "—";

  if (!engagement) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6">
        {error ? (
          <div className="text-sm text-rose-600 dark:text-rose-300">{error}</div>
        ) : (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6">
      <div className="lg:grid lg:gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="hidden lg:block self-start sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
          <EngagementsSidePane
            selectedId={id ?? null}
            onPick={(eid) => navigate(`${handlePrefix}/engagements/${eid}`)}
          />
        </aside>
        <section className="space-y-5 min-w-0">
      <Breadcrumb
        segments={[
          { label: "Engagements", href: `${handlePrefix}/engagements` },
          { label: engagement.title },
        ]}
      />

      <header className="flex items-start gap-3">
        <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
          <Icon className="w-5 h-5" strokeWidth={2.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-orange-200 dark:bg-orange-700/40 text-orange-800 dark:text-orange-200">
              {label}
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-bold ${statusTint(engagement.status)}`}>
              {engagement.status}
            </span>
            {engagement.due_at && (
              <span className="text-[11px] text-slate-600 dark:text-slate-300">
                Due {new Date(engagement.due_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            )}
          </div>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100 break-words">
            {engagement.title}
          </h1>
          {engagement.description && (
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
              {engagement.description}
            </p>
          )}
          {engagement.detection_reason && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 italic">
              Detected via {engagement.detection_source}: {engagement.detection_reason}
            </p>
          )}
        </div>
      </header>

      {engagement.status === "active" && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => flip("completed")}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" />
            Mark completed
          </button>
          <button
            type="button"
            onClick={() => flip("postponed")}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            <PauseCircle className="w-3.5 h-3.5" />
            Postpone
          </button>
          <button
            type="button"
            onClick={() => flip("abandoned")}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold px-3 py-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:text-rose-700 dark:hover:text-rose-300 disabled:opacity-50"
          >
            <XIcon className="w-3.5 h-3.5" />
            Abandon
          </button>
        </div>
      )}

      {engagement.status !== "active" && (
        <button
          type="button"
          onClick={() => flip("active")}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
        >
          <Play className="w-3.5 h-3.5" />
          Reactivate
        </button>
      )}

      {weaveInsights.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            Memso woven inferences
          </h2>
          <ol className="space-y-1.5">
            {weaveInsights.map((i) => {
              const conf = typeof i.confidence === "number" ? Math.round(i.confidence * 100) : null;
              const isInferred = conf !== null && conf < 85;
              return (
                <li
                  key={i.id}
                  className={`rounded-xl px-3 py-2 text-sm border ${
                    isInferred
                      ? "bg-violet-50 dark:bg-violet-950/20 border-dashed border-violet-300/70 dark:border-violet-700/40"
                      : "bg-violet-50 dark:bg-violet-950/20 border-violet-200/60 dark:border-violet-800/40"
                  } text-slate-800 dark:text-slate-100`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300 mb-1 flex items-center gap-2 flex-wrap">
                    <span>{i.kind.replace(/_/g, " ")}</span>
                    {conf !== null && <span className="opacity-70">· {conf}% confidence</span>}
                    {isInferred && <span className="opacity-70">· inferred</span>}
                  </div>
                  <div className="font-medium">{i.title}</div>
                  {i.body && (
                    <div className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-300 line-clamp-3">
                      {i.body}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Activity ({activity.length})
        </h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && noteText.trim()) addNote();
            }}
            placeholder="Add a note..."
            className="flex-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={addNote}
            disabled={busy || !noteText.trim()}
            className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-2 disabled:opacity-50"
          >
            Add
          </button>
        </div>
        {activity.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 italic">
            No activity yet. Add a note or wait for new packets to arrive.
          </p>
        ) : (
          <ul className="space-y-2">
            {activity.map((a) => (
              <ActivityCard key={a.id} activity={a} />
            ))}
          </ul>
        )}
      </section>
        </section>
      </div>
    </div>
  );
}

/** Compact left-rail list of engagements for the detail page's wide
 *  layout. Loads the same active engagements as the index, highlights
 *  the current one, and switches via onPick. Hidden on < lg by the
 *  caller. */
function EngagementsSidePane({
  selectedId,
  onPick,
}: {
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  const [items, setItems] = useState<Engagement[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api
      .engagements({ status: "active", limit: 60 })
      .then((d) => setItems(d.engagements))
      .catch((e: Error) => setError(e.message));
  }, [selectedId]);
  return (
    <nav
      aria-label="Other engagements"
      className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-2 space-y-0.5"
    >
      <div className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        Engagements
      </div>
      {error && (
        <div className="px-2 text-xs text-rose-600 dark:text-rose-300">{error}</div>
      )}
      <ul className="space-y-0.5 max-h-[60vh] overflow-y-auto">
        {items.map((e) => {
          const Icon = KIND_ICON[(e.kind as EngagementKind) ?? "other"] ?? Sparkles;
          const isActive = e.id === selectedId;
          return (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => onPick(e.id)}
                aria-current={isActive ? "page" : undefined}
                className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                  isActive
                    ? "bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100"
                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2 leading-snug">{e.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ActivityCard({ activity }: { activity: ActivityRow }) {
  return (
    <li className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-3 flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          {activity.kind.replace(/_/g, " ")}
        </span>
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {new Date(activity.occurred_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </span>
      </div>
      <div className="text-sm text-slate-900 dark:text-slate-100 break-words">
        {activity.summary}
      </div>
    </li>
  );
}
